/** XeAgents.ai**/
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";
import { formatRelativeTime } from "@/lib/date-format";
import {
  Ticket,
  Clock,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Search,
  Filter,
  RefreshCw,
  ChevronRight,
  User,
  Calendar,
  Tag,
  AlertTriangle,
  XCircle,
  Loader2,
  MoreHorizontal,
  UserPlus,
  ArrowUpDown,
  Building,
  BarChart3,
  TrendingUp,
  Users,
} from "lucide-react";

interface AdminUser {
  id: string;
  name: string;
  email: string;
}

interface SupportTicket {
  id: string;
  ticketNumber: number;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  resolution: string | null;
  attachments: string[];
  assignedTo: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  organizationId: string;
  userName: string;
  userEmail: string;
  organizationName: string;
}

interface TicketsResponse {
  tickets: SupportTicket[];
  total: number;
  limit: number;
  offset: number;
}

interface HelpdeskStats {
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  byCategory: Record<string, number>;
  avgResolutionTimeHours: number;
  unassignedCount: number;
  last7Days: {
    newTickets: number;
    resolvedTickets: number;
  };
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  open: { label: "Open", color: "bg-blue-500", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-yellow-500", icon: Clock },
  waiting_on_customer: { label: "Waiting on Customer", color: "bg-purple-500", icon: MessageSquare },
  resolved: { label: "Resolved", color: "bg-green-500", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-gray-500", icon: XCircle },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-400" },
  medium: { label: "Medium", color: "bg-blue-400" },
  high: { label: "High", color: "bg-orange-500" },
  urgent: { label: "Urgent", color: "bg-red-500" },
};

const categoryConfig: Record<string, { label: string }> = {
  general: { label: "General" },
  billing: { label: "Billing" },
  technical: { label: "Technical" },
  feature_request: { label: "Feature Request" },
  bug_report: { label: "Bug Report" },
  account: { label: "Account" },
};

export default function AdminHelpDeskPage() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [assignedFilter, setAssignedFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [resolution, setResolution] = useState("");

  // Fetch admin tickets
  const { data: ticketsData, isLoading, refetch } = useQuery<TicketsResponse>({
    queryKey: ["admin-helpdesk-tickets", statusFilter, priorityFilter, categoryFilter, assignedFilter, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (assignedFilter !== "all") params.append("assignedTo", assignedFilter);
      if (searchQuery) params.append("search", searchQuery);

      const url = `/api/admin/helpdesk/tickets${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await fetch(url, {
        headers: {
          Authorization: AuthStorage.getAuthHeader() || "",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch tickets");
      return res.json();
    },
  });

  // Fetch stats
  const { data: stats } = useQuery<HelpdeskStats>({
    queryKey: ["admin-helpdesk-stats"],
    queryFn: async () => {
      const res = await fetch("/api/admin/helpdesk/stats", {
        headers: {
          Authorization: AuthStorage.getAuthHeader() || "",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch stats");
      return res.json();
    },
  });

  // Fetch admin users for assignment
  const { data: adminUsers = [] } = useQuery<AdminUser[]>({
    queryKey: ["admin-helpdesk-admins"],
    queryFn: async () => {
      const res = await fetch("/api/admin/helpdesk/admins", {
        headers: {
          Authorization: AuthStorage.getAuthHeader() || "",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch admins");
      return res.json();
    },
  });

  // Assign ticket mutation
  const assignMutation = useMutation({
    mutationFn: async ({ ticketId, assignedTo }: { ticketId: string; assignedTo: string | null }) => {
      const res = await apiRequest("PATCH", `/api/admin/helpdesk/tickets/${ticketId}/assign`, {
        assignedTo,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t("helpdesk.ticketAssigned"),
        description: t("helpdesk.ticketAssignedDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ["admin-helpdesk-tickets"] });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update status mutation
  const statusMutation = useMutation({
    mutationFn: async ({ ticketId, status }: { ticketId: string; status: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/helpdesk/tickets/${ticketId}/status`, {
        status,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t("helpdesk.statusUpdated"),
        description: t("helpdesk.statusUpdatedDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ["admin-helpdesk-tickets"] });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update priority mutation
  const priorityMutation = useMutation({
    mutationFn: async ({ ticketId, priority }: { ticketId: string; priority: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/helpdesk/tickets/${ticketId}/priority`, {
        priority,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t("helpdesk.priorityUpdated"),
        description: t("helpdesk.priorityUpdatedDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ["admin-helpdesk-tickets"] });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Resolve ticket mutation
  const resolveMutation = useMutation({
    mutationFn: async ({ ticketId, resolution }: { ticketId: string; resolution: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/helpdesk/tickets/${ticketId}/resolve`, {
        resolution,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t("helpdesk.ticketResolved"),
        description: t("helpdesk.ticketResolvedDesc"),
      });
      setResolveDialogOpen(false);
      setSelectedTicket(null);
      setResolution("");
      queryClient.invalidateQueries({ queryKey: ["admin-helpdesk-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["admin-helpdesk-stats"] });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const tickets = ticketsData?.tickets || [];

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const config = priorityConfig[priority] || priorityConfig.medium;
    return (
      <Badge variant="outline" className={`border-0 ${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  const handleResolve = () => {
    if (!selectedTicket || !resolution.trim()) {
      toast({
        title: t("helpdesk.error"),
        description: t("helpdesk.resolutionRequired"),
        variant: "destructive",
      });
      return;
    }
    resolveMutation.mutate({ ticketId: selectedTicket.id, resolution });
  };

  const currentUser = AuthStorage.getUser();

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="h-6 w-6" />
            {t("helpdesk.adminTitle")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("helpdesk.adminDescription")}
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("common.refresh")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.openTickets")}</CardTitle>
            <AlertCircle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(stats?.byStatus?.open || 0) + (stats?.byStatus?.in_progress || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.unassignedCount || 0} {t("helpdesk.unassigned")}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.urgentTickets")}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {(stats?.byPriority?.urgent || 0) + (stats?.byPriority?.high || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("helpdesk.needsAttention")}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.avgResolutionTime")}</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.avgResolutionTimeHours || 0}h
            </div>
            <p className="text-xs text-muted-foreground">
              {t("helpdesk.averageTime")}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.last7Days")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              +{stats?.last7Days?.newTickets || 0} / -{stats?.last7Days?.resolvedTickets || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("helpdesk.newResolved")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-lg">{t("helpdesk.allTickets")}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t("helpdesk.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full md:w-48"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder={t("helpdesk.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("helpdesk.allStatuses")}</SelectItem>
                  <SelectItem value="open">{t("helpdesk.statusOpen")}</SelectItem>
                  <SelectItem value="in_progress">{t("helpdesk.statusInProgress")}</SelectItem>
                  <SelectItem value="waiting_on_customer">{t("helpdesk.statusWaiting")}</SelectItem>
                  <SelectItem value="resolved">{t("helpdesk.statusResolved")}</SelectItem>
                  <SelectItem value="closed">{t("helpdesk.statusClosed")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder={t("helpdesk.priority")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("helpdesk.allPriorities")}</SelectItem>
                  <SelectItem value="urgent">{t("helpdesk.priorityUrgent")}</SelectItem>
                  <SelectItem value="high">{t("helpdesk.priorityHigh")}</SelectItem>
                  <SelectItem value="medium">{t("helpdesk.priorityMedium")}</SelectItem>
                  <SelectItem value="low">{t("helpdesk.priorityLow")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder={t("helpdesk.assignee")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("helpdesk.allAssignees")}</SelectItem>
                  <SelectItem value="unassigned">{t("helpdesk.unassigned")}</SelectItem>
                  {adminUsers.map((admin) => (
                    <SelectItem key={admin.id} value={admin.id}>
                      {admin.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8">
              <Ticket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">{t("helpdesk.noTicketsFound")}</h3>
              <p className="text-muted-foreground mt-1">{t("helpdesk.adjustFilters")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">#</TableHead>
                  <TableHead>{t("helpdesk.subject")}</TableHead>
                  <TableHead>{t("helpdesk.customer")}</TableHead>
                  <TableHead>{t("helpdesk.priority")}</TableHead>
                  <TableHead>{t("helpdesk.status")}</TableHead>
                  <TableHead>{t("helpdesk.assignee")}</TableHead>
                  <TableHead>{t("helpdesk.lastActivity")}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow key={ticket.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell
                      className="font-mono text-sm"
                      onClick={() => setLocation(`/app/helpdesk/${ticket.id}`)}
                    >
                      #{ticket.ticketNumber}
                    </TableCell>
                    <TableCell onClick={() => setLocation(`/app/helpdesk/${ticket.id}`)}>
                      <div className="font-medium">{ticket.subject}</div>
                      <div className="text-sm text-muted-foreground">
                        <Badge variant="outline" className="mr-1">
                          {categoryConfig[ticket.category]?.label || ticket.category}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell onClick={() => setLocation(`/app/helpdesk/${ticket.id}`)}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-sm font-medium">{ticket.userName}</div>
                          <div className="text-xs text-muted-foreground">{ticket.organizationName}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={ticket.priority}
                        onValueChange={(value) => priorityMutation.mutate({ ticketId: ticket.id, priority: value })}
                      >
                        <SelectTrigger className="w-28 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t("helpdesk.priorityLow")}</SelectItem>
                          <SelectItem value="medium">{t("helpdesk.priorityMedium")}</SelectItem>
                          <SelectItem value="high">{t("helpdesk.priorityHigh")}</SelectItem>
                          <SelectItem value="urgent">{t("helpdesk.priorityUrgent")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={ticket.status}
                        onValueChange={(value) => statusMutation.mutate({ ticketId: ticket.id, status: value })}
                      >
                        <SelectTrigger className="w-40 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">{t("helpdesk.statusOpen")}</SelectItem>
                          <SelectItem value="in_progress">{t("helpdesk.statusInProgress")}</SelectItem>
                          <SelectItem value="waiting_on_customer">{t("helpdesk.statusWaiting")}</SelectItem>
                          <SelectItem value="resolved">{t("helpdesk.statusResolved")}</SelectItem>
                          <SelectItem value="closed">{t("helpdesk.statusClosed")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={ticket.assignedTo || "unassigned"}
                        onValueChange={(value) =>
                          assignMutation.mutate({
                            ticketId: ticket.id,
                            assignedTo: value === "unassigned" ? null : value,
                          })
                        }
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue placeholder={t("helpdesk.unassigned")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">{t("helpdesk.unassigned")}</SelectItem>
                          {adminUsers.map((admin) => (
                            <SelectItem key={admin.id} value={admin.id}>
                              {admin.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(ticket.lastActivityAt)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{t("helpdesk.actions")}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setLocation(`/app/helpdesk/${ticket.id}`)}>
                            <MessageSquare className="h-4 w-4 mr-2" />
                            {t("helpdesk.viewDetails")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              assignMutation.mutate({
                                ticketId: ticket.id,
                                assignedTo: currentUser?.id || null,
                              })
                            }
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            {t("helpdesk.assignToMe")}
                          </DropdownMenuItem>
                          {ticket.status !== "resolved" && ticket.status !== "closed" && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setResolveDialogOpen(true);
                              }}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              {t("helpdesk.resolve")}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("helpdesk.resolveTicket")}</DialogTitle>
            <DialogDescription>
              {selectedTicket && (
                <span>
                  {t("helpdesk.resolvingTicket")} #{selectedTicket.ticketNumber}: {selectedTicket.subject}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resolution">{t("helpdesk.resolution")} *</Label>
              <Textarea
                id="resolution"
                placeholder={t("helpdesk.resolutionPlaceholder")}
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleResolve} disabled={resolveMutation.isPending}>
              {resolveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {t("helpdesk.markResolved")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
